import { useState } from "react";
import { Check, LoaderCircle, Plus, Save, X } from "lucide-react";
import {
  MAX_QUALIFICATION_CRITERIA,
  type OfferProfile,
  type QualificationCriterion,
} from "@shared/lib/offer-profiles-shared";
import { useT } from "@shared/client/locale";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";
import { Checkbox } from "@shared/components/ui/checkbox";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { Textarea } from "@shared/components/ui/textarea";

/**
 * La fiche de ciblage d'une offre : ce que l'agent a compris, corrigé par
 * l'utilisateur. Rendue dans la carte de son offre (« Mes offres ») — une
 * fiche n'existe jamais seule, elle appartient toujours à une offre.
 */

/** Un critère en cours d'édition : les mots à chercher se saisissent séparés par des virgules. */
type CriterionDraft = Omit<QualificationCriterion, "search_terms"> & { key: number; search_terms: string };

function toDrafts(criteria: QualificationCriterion[]): CriterionDraft[] {
  return criteria.map((criterion, index) => ({ ...criterion, key: index, search_terms: criterion.search_terms.join(", ") }));
}

function fromDrafts(drafts: CriterionDraft[]): QualificationCriterion[] {
  return drafts
    .filter((draft) => draft.label.trim())
    .map((draft) => ({
      label: draft.label.trim(),
      good_sign: draft.good_sign.trim(),
      decisive: draft.decisive,
      search_terms: draft.search_terms.split(",").map((term) => term.trim()).filter(Boolean),
    }));
}
export function OfferProfileForm({ profile, onSaved, onCancel }: { profile: OfferProfile; onSaved?: () => void; onCancel?: () => void }) {
  const t = useT().settings.profileForm;
  const [offerName, setOfferName] = useState(profile.offer_name || "");
  const [offerSummary, setOfferSummary] = useState(profile.offer_summary || "");
  const [prioritySegment, setPrioritySegment] = useState(profile.priority_segment || "");
  const [decisionMaker, setDecisionMaker] = useState(profile.decision_maker || "");
  const [favorableEvent, setFavorableEvent] = useState(profile.favorable_event || "");
  const [exclusions, setExclusions] = useState(profile.exclusions || "");
  const [userNotes, setUserNotes] = useState(profile.user_notes || "");
  const [criteria, setCriteria] = useState(() => toDrafts(profile.qualification_criteria ?? []));
  const [nextKey, setNextKey] = useState(() => (profile.qualification_criteria ?? []).length);

  function updateCriterion(key: number, patch: Partial<CriterionDraft>) {
    setCriteria((current) => current.map((draft) => (draft.key === key ? { ...draft, ...patch } : draft)));
  }
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function save(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    setError("");
    const response = await fetch(`/api/offer-profiles/${profile.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        offer_name: offerName,
        offer_summary: offerSummary,
        priority_segment: prioritySegment,
        decision_maker: decisionMaker,
        favorable_event: favorableEvent,
        exclusions,
        user_notes: userNotes,
        qualification_criteria: fromDrafts(criteria),
      }),
    });
    setBusy(false);
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { error?: string } | null;
      setError(body?.error || t.failed);
      return;
    }
    if (onSaved) onSaved();
    else setMessage(t.saved);
  }

  const field = `offer-${profile.id}`;

  return (
    <form className="grid gap-4 md:grid-cols-2" onSubmit={save}>
      <div className="grid gap-2">
        <Label htmlFor={`${field}-name`}>{t.name}</Label>
        <Input id={`${field}-name`} onChange={(event) => setOfferName(event.target.value)} value={offerName} />
      </div>
      <div className="grid gap-2">
        <Label htmlFor={`${field}-decision`}>{t.decisionMaker}</Label>
        <Input id={`${field}-decision`} onChange={(event) => setDecisionMaker(event.target.value)} value={decisionMaker} />
      </div>

      <div className="grid gap-2 md:col-span-2">
        <Label htmlFor={`${field}-summary`}>{t.summary}</Label>
        <Textarea
          id={`${field}-summary`}
          onChange={(event) => setOfferSummary(event.target.value)}
          placeholder={t.summaryPlaceholder}
          rows={3}
          value={offerSummary}
        />
      </div>

      <div className="grid gap-2 md:col-span-2">
        <Label htmlFor={`${field}-segment`}>{t.segment}</Label>
        <Textarea
          id={`${field}-segment`}
          onChange={(event) => setPrioritySegment(event.target.value)}
          placeholder={t.segmentPlaceholder}
          rows={2}
          value={prioritySegment}
        />
      </div>

      <div className="grid gap-2 md:col-span-2">
        <Label htmlFor={`${field}-event`}>{t.event}</Label>
        <Input
          id={`${field}-event`}
          onChange={(event) => setFavorableEvent(event.target.value)}
          placeholder={t.eventPlaceholder}
          value={favorableEvent}
        />
      </div>

      <div className="grid gap-2 md:col-span-2">
        <Label htmlFor={`${field}-exclusions`}>{t.exclusions}</Label>
        <Textarea
          id={`${field}-exclusions`}
          onChange={(event) => setExclusions(event.target.value)}
          placeholder={t.exclusionsPlaceholder}
          rows={2}
          value={exclusions}
        />
      </div>

      <fieldset className="m-0 grid gap-2 border-0 p-0 md:col-span-2">
        <legend className="mb-1 text-sm leading-none font-medium">{t.criteria.legend}</legend>
        <p className="m-0 text-[12.5px] text-muted-foreground">
          {t.criteria.help}
        </p>
        {criteria.map((draft) => (
          <div className="grid gap-2 rounded-lg border p-3 md:grid-cols-[1fr_1.4fr_auto]" key={draft.key}>
            <Input
              aria-label={t.criteria.label}
              id={`${field}-criterion-${draft.key}-label`}
              onChange={(event) => updateCriterion(draft.key, { label: event.target.value })}
              placeholder={t.criteria.labelPlaceholder}
              value={draft.label}
            />
            <Input
              aria-label={t.criteria.goodSign}
              id={`${field}-criterion-${draft.key}-good`}
              onChange={(event) => updateCriterion(draft.key, { good_sign: event.target.value })}
              placeholder={t.criteria.goodSignPlaceholder}
              value={draft.good_sign}
            />
            <Button
              aria-label={t.criteria.remove(draft.label)}
              className="max-md:hidden"
              onClick={() => setCriteria((current) => current.filter((item) => item.key !== draft.key))}
              size="icon"
              type="button"
              variant="ghost"
            >
              <X />
            </Button>
            <Input
              aria-label={t.criteria.searchTerms}
              className="md:col-span-2"
              id={`${field}-criterion-${draft.key}-terms`}
              onChange={(event) => updateCriterion(draft.key, { search_terms: event.target.value })}
              placeholder={t.criteria.searchTermsPlaceholder}
              value={draft.search_terms}
            />
            <div className="flex items-center justify-between gap-3">
              <Label className="font-normal" htmlFor={`${field}-criterion-${draft.key}-decisive`}>
                <Checkbox
                  checked={draft.decisive}
                  id={`${field}-criterion-${draft.key}-decisive`}
                  onCheckedChange={(checked) => updateCriterion(draft.key, { decisive: checked === true })}
                />
                {t.criteria.decisive}
              </Label>
              <Button
                className="md:hidden"
                onClick={() => setCriteria((current) => current.filter((item) => item.key !== draft.key))}
                size="sm"
                type="button"
                variant="ghost"
              >
                <X /> {t.criteria.removeShort}
              </Button>
            </div>
          </div>
        ))}
        {criteria.length < MAX_QUALIFICATION_CRITERIA && (
          <Button
            className="justify-self-start"
            onClick={() => {
              setCriteria((current) => [...current, { key: nextKey, label: "", good_sign: "", decisive: false, search_terms: "" }]);
              setNextKey(nextKey + 1);
            }}
            size="sm"
            type="button"
            variant="outline"
          >
            <Plus /> {t.criteria.add}
          </Button>
        )}
      </fieldset>

      <div className="grid gap-2 md:col-span-2">
        <Label htmlFor={`${field}-notes`}>{t.notes}</Label>
        <Textarea
          id={`${field}-notes`}
          onChange={(event) => setUserNotes(event.target.value)}
          placeholder={t.notesPlaceholder}
          rows={3}
          value={userNotes}
        />
      </div>

      <div className="flex flex-wrap items-center gap-4 md:col-span-2">
        <Button disabled={busy} type="submit">
          {busy ? <LoaderCircle className="animate-spin" /> : <Save />} {t.save}
        </Button>
        {onCancel && (
          <Button onClick={onCancel} type="button" variant="ghost">
            {t.cancel}
          </Button>
        )}
        {message && (
          <p className="m-0 flex items-center gap-2 text-[12.5px] font-medium text-success">
            <Check className="size-4" aria-hidden="true" /> {message}
          </p>
        )}
      </div>

      {error && (
        <Alert className="md:col-span-2" variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </form>
  );
}
